import upperFirst from "lodash/upperFirst";

import { OnboardingStepName } from "@/constants/Onboarding";
import { FAIconName } from "@/ui/core";
import type { ButtonVariant } from "@/ui/core/buttons/Button";
import { OnboardingIllustrationName } from "@/ui/theme/icons/illustrations/onboarding";

interface OnboardingStepButton {
  rightIcon?: FAIconName;
  title: string;
  variant: ButtonVariant;
}

interface OnboardingStepContent {
  button: OnboardingStepButton;
  description: string;
  duration: string;
  iconName: OnboardingIllustrationName;
  listItems: string[];
  title: string;
}

interface StepDynamicContent {
  description: string;
  listItems: string[];
  title: string;
}

type StepDynamicContentBuilder = (patientName?: string) => StepDynamicContent;

const StepDynamicContentRecord: Record<
  OnboardingStepName,
  StepDynamicContentBuilder
> = {
  [OnboardingStepName.CareTeam]: (patientName) => {
    const whose = patientName ? `${patientName}'s` : "your";

    return {
      description: `Now you'll work with your admissions team to select the providers that best meet ${whose} needs.`,
      listItems: ["Book your first visit"],
      title: `${upperFirst(whose)} care team`,
    };
  },
  [OnboardingStepName.ClinicalPicture]: (patientName) => {
    const whose = patientName ? `${patientName}'s` : "your";
    const pronoun = patientName ? "their" : "your";

    return {
      description: `A secure intake survey about ${whose} health and history. We'll use this information to personalize ${pronoun} care team and treatment plan.`,
      listItems: [],
      title: `${upperFirst(whose)} clinical picture`,
    };
  },
  [OnboardingStepName.Initial]: () => ({
    description: "",
    listItems: [],
    title: "You reached out to Equip",
  }),
  [OnboardingStepName.Profile]: (patientName) => {
    const whose = patientName ? `${patientName}'s` : "your";

    return {
      description: `The essentials ${whose} care team needs before they can plan anything.`,
      listItems: [
        "Verify demographic, contact, & insurance info",
        "Set up permissions",
      ],
      title: `${upperFirst(whose)} profile`,
    };
  },
  [OnboardingStepName.Treatment]: (patientName) => ({
    description: patientName
      ? `A calm look at the phases and the weekly rhythm ahead for ${patientName}.`
      : "A calm look at the phases and the weekly rhythm ahead.",
    listItems: [
      "See the four phases ahead",
      "Meet your weekly rhythm",
      "Say yes to your admit date",
    ],
    title: "What treatment looks like",
  }),
  [OnboardingStepName.YourWhy]: (patientName) => {
    const whose = patientName ? `${patientName}'s` : "your";

    return {
      description:
        "Take a moment to reflect on how you're feeling about starting treatment with Equip.",
      listItems: [
        "Set yourself up for the hard days",
        `Your response will be shared with ${whose} care team so they can meet you where you are`,
      ],
      title: "Your why",
    };
  },
};

interface StepStaticContent {
  button: OnboardingStepButton;
  duration: string;
  iconName: OnboardingIllustrationName;
}

const StepStaticContentRecord: Record<OnboardingStepName, StepStaticContent> = {
  [OnboardingStepName.CareTeam]: {
    button: {
      rightIcon: FAIconName.ArrowRight,
      title: "Contact admissions team",
      variant: "primary",
    },
    duration: "About 4 min",
    iconName: OnboardingIllustrationName.Placeholder,
  },
  [OnboardingStepName.ClinicalPicture]: {
    button: {
      rightIcon: FAIconName.ExternalLink,
      title: "Open intake survey",
      variant: "primary",
    },
    duration: "About 10 min",
    iconName: OnboardingIllustrationName.Placeholder,
  },
  [OnboardingStepName.Initial]: {
    button: { title: "", variant: "primary" },
    duration: "",
    iconName: OnboardingIllustrationName.Placeholder,
  },
  [OnboardingStepName.Profile]: {
    button: {
      rightIcon: FAIconName.ArrowRight,
      title: "Start now",
      variant: "primary",
    },
    duration: "About 7 min",
    iconName: OnboardingIllustrationName.Placeholder,
  },
  [OnboardingStepName.Treatment]: {
    button: {
      rightIcon: FAIconName.ArrowRight,
      title: "Start now",
      variant: "primary",
    },
    duration: "About 5 min",
    iconName: OnboardingIllustrationName.Placeholder,
  },
  [OnboardingStepName.YourWhy]: {
    button: {
      rightIcon: FAIconName.ArrowRight,
      title: "Start now",
      variant: "primary",
    },
    duration: "About 4 min",
    iconName: OnboardingIllustrationName.Placeholder,
  },
};

interface GetOnboardingStepContentParams {
  patientName?: string;
  stepName: OnboardingStepName;
}

const getOnboardingStepContent = ({
  patientName,
  stepName,
}: GetOnboardingStepContentParams): OnboardingStepContent => {
  const staticContent = StepStaticContentRecord[stepName];
  const dynamicContent = StepDynamicContentRecord[stepName](patientName);

  return {
    button: staticContent.button,
    description: dynamicContent.description,
    duration: staticContent.duration,
    iconName: staticContent.iconName,
    listItems: dynamicContent.listItems,
    title: dynamicContent.title,
  };
};

export { getOnboardingStepContent };
